import type { IOwnerPhone } from '@features/account/types'
import countriesData from '@data/form/countries.json'
import { extractCountryCodeFromPhoneNumber } from '@utils/formattingUtils'

export const DEFAULT_ACCOUNT_COUNTRY_CODE = 'USA'

export function findCountryByCode(countryCode: string): any {
  try {
    return countriesData.find((c: any) => c.countryCode === countryCode || c.countryCode.startsWith(countryCode))
  } catch (error) {
    console.warn('Failed to load countries data:', error)
    return null
  }
}

/** Stored/API value → national digits for controlled phone inputs. */
export function parseStoredPhoneToNationalNumber(phoneNumber: string, countryCode: string): string {
  if (!phoneNumber) return ''

  const cc = countryCode || DEFAULT_ACCOUNT_COUNTRY_CODE
  if (!phoneNumber.startsWith('+')) {
    return phoneNumber
  }
  if (phoneNumber.startsWith('+1') && (cc === 'US' || cc === 'USA' || cc === 'CA' || cc === 'CAN')) {
    const nanpTen = phoneNumber.match(/^\+1(\d{10})$/)
    if (nanpTen) {
      return nanpTen[1]
    }
    return phoneNumber.slice(1)
  }
  const country = findCountryByCode(cc)
  if (country?.extension) {
    const extension = country.extension.replace('+', '').replace('-', '')
    if (phoneNumber.startsWith('+' + extension)) {
      return phoneNumber.slice(extension.length + 1)
    }
  }
  return phoneNumber
}

/**
 * Normalize a phone field to the national digits-only form expected by the my-account PUT.
 *
 * The Voyager request carries the country code separately, so the digits must NOT include the
 * `+1` (or other country) prefix. Idempotent for any of: national digits ("5551234567"),
 * E.164 ("+15551234567"), or formatted ("+1 (555) 123-4567"). For NANP, also tolerates an
 * accidental leading "1" (national prefix) by stripping it.
 *
 * Also used for InternationalPhone's `value` prop in the edit form — the component accepts
 * national digits when `defaultCountry` is supplied.
 */
export function reconstructFullPhoneNumber(input: string, countryCode: string): string {
  if (!input || !input.trim()) {
    return ''
  }

  const cc = countryCode || DEFAULT_ACCOUNT_COUNTRY_CODE
  const allDigits = input.replace(/\D/g, '')
  if (!allDigits) return ''

  if (cc === 'US' || cc === 'USA' || cc === 'CA' || cc === 'CAN') {
    if (/^0+$/.test(allDigits)) return ''
    // Drop a leading "1" (NANP country/national prefix) so callers can pass either national
    // digits ("5551234567") or an E.164 value ("+15551234567" → "15551234567") without the
    // country code surviving into the backend payload.
    const national = allDigits.length === 11 && allDigits.startsWith('1') ? allDigits.slice(1) : allDigits
    return national.length <= 10 ? national : national.slice(0, 10)
  }

  const country = findCountryByCode(cc)
  if (country?.extension) {
    const dial = country.extension.replace(/\D/g, '')
    return dial && allDigits.startsWith(dial) ? allDigits.slice(dial.length) : allDigits
  }

  return allDigits
}

/**
 * Voyager identifies an existing phone row by `phoneId` / `phonePk`. Sending the request
 * without those ids causes Voyager to ADD a new phone row instead of updating the existing
 * one (per backend dev guidance — observed phone numbers being added vs updated in TRIP).
 *
 * So when the loaded owner had a row of this phone type, always carry its ids forward in the
 * outgoing payload — even if the user edited the digits or changed the country.
 */
function applyBaselinePhoneIds(phone: IOwnerPhone, baseline: IOwnerPhone | undefined): IOwnerPhone {
  if (!baseline) return phone
  return {
    ...phone,
    phoneId: baseline.phoneId || phone.phoneId || '',
    phonePk: baseline.phonePk || phone.phonePk || '',
  }
}

/**
 * Normalizes phone values for PUT /mkapi/v1/myAccount. The output `phoneNumber` is national
 * digits only (no "+1") — the country code travels in the sibling `countryCode` field.
 *
 * When `baselineFromOwner` is supplied, the original Voyager `phoneId` / `phonePk` are
 * preserved so Voyager updates the existing row instead of inserting a duplicate.
 */
export function preprocessPhone(
  input: IOwnerPhone | string | undefined,
  defaultCountryCode?: string,
  baselineFromOwner?: IOwnerPhone,
): IOwnerPhone | undefined {
  const countryCode = defaultCountryCode || DEFAULT_ACCOUNT_COUNTRY_CODE

  if (input == null || input === '+1undefined') {
    return applyBaselinePhoneIds(
      { phoneId: '', phonePk: '', phoneNumber: '', countryCode: baselineFromOwner?.countryCode || countryCode },
      baselineFromOwner,
    )
  }

  if (typeof input === 'string') {
    if (!input.trim()) {
      return applyBaselinePhoneIds(
        { phoneId: '', phonePk: '', phoneNumber: '', countryCode: baselineFromOwner?.countryCode || countryCode },
        baselineFromOwner,
      )
    }
    const extractedCountryCode = input.startsWith('+') ? extractCountryCodeFromPhoneNumber(input) : countryCode
    const cc = extractedCountryCode || countryCode
    const nationalDigits = reconstructFullPhoneNumber(input, cc)
    return applyBaselinePhoneIds(
      { phoneId: '', phonePk: '', phoneNumber: nationalDigits, countryCode: cc },
      baselineFromOwner,
    )
  }

  const phone = input
  const cc = phone.countryCode || countryCode

  if (!phone.phoneNumber || !phone.phoneNumber.trim()) {
    return applyBaselinePhoneIds(
      { phoneId: phone.phoneId || '', phonePk: phone.phonePk || '', phoneNumber: '', countryCode: cc },
      baselineFromOwner,
    )
  }

  const nationalDigits = reconstructFullPhoneNumber(phone.phoneNumber, cc)
  return applyBaselinePhoneIds(
    { phoneId: phone.phoneId || '', phonePk: phone.phonePk || '', phoneNumber: nationalDigits, countryCode: cc },
    baselineFromOwner,
  )
}
